import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { RadialBarsVisualization } from '../components/waveform/RadialBarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function AvatarLayout({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, pl, ps } = useOrientation(orientation)
  const imageSize = orientation === 'square' ? 236 : 300
  const diameterAdd = orientation === 'square' ? 200 : 300

  return (
    <AbsoluteFill className="">
      {clipScene.showZealousLogo && (
        <ZealousSvg
          className={cn(
            'absolute right-[60px] h-[49px] opacity-50',
            l && 'bottom-[63px]',
            ps && 'top-[60px]',
          )}
          color={style.logo.color}
        />
      )}

      <div className={cn(`flex h-full`, l && 'flex-row', ps && 'flex-col')}>
        <div
          className={cn(
            `flex flex-col items-center justify-center`,
            p && 'space-y-[230px] pt-[186px]',
            s && 'space-y-[157px] pt-[141px]',
            l && 'h-full shrink-0 space-y-[193px] px-[206px]',
          )}
        >
          <div className="relative">
            <div className="absolute top-0 flex h-full w-full items-center justify-center">
              <RadialBarsVisualization
                color={style.waveform.color}
                diameter={imageSize + diameterAdd}
                gapSize={7}
                innerRadius={imageSize / 2 + 22}
                lineThickness={15}
                mediaStart={clipScene.mediaStart}
                mediaUrl={sceneAudioSrc(clipScene)}
                waveformUrl={clipScene.waveformUrl}
                roundness={7}
              />
            </div>

            <FallbackImg
              src={speaker?.avatarUrl}
              style={{ height: imageSize, width: imageSize }}
              className="rounded-full object-cover"
            />
          </div>

          <p
            style={{ ...style.speakerUsername }}
            className={cn(
              'leading-[46px] font-[500]',
              s && 'text-[46px]',
              pl && 'text-[50px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        <div
          className={cn(
            `m-auto flex h-full items-center`,
            l && 'px-[120px]',
            ps && 'px-[80px]',
          )}
        >
          {children}
        </div>
      </div>

      <div
        className="w-full border opacity-50"
        style={{ borderColor: style.border.color }}
      />

      <div
        className={cn(
          'flex items-center p-[32px]',
          s && 'space-x-[32px] text-[34px] leading-[47.6px]',
          pl && 'space-x-[50px] p-[50px] text-[38px] leading-[58px]',
        )}
      >
        <FallbackImg
          src={clipScene.imageUrl}
          className={cn(
            'aspect-square rounded-full object-cover',
            s && 'h-[100px] w-[100px]',
            pl && 'h-[130px] w-[130px]',
          )}
        />

        <div className="space-y-2">
          <p
            style={{ ...style.title }}
            className="line-clamp-1 text-left font-[600]"
          >
            {clipScene.title}
          </p>

          <p
            style={{ ...style.subtitle }}
            className="line-clamp-1 text-left font-[500]"
          >
            {clipScene.subtitle}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  )
}

export const avatarLayoutInfo: LayoutInfo = {
  name: 'avatar',
  Component: AvatarLayout,
  defaultScene: {
    ...defaultClipScene,
    layout: 'avatar',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'secondary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    transcription: {
      color: 'secondary',
      fontFamily: 'primary',
      highlightColor: 'accent',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
      highlightType: 'last-word',
      fontSize: 60,
      scrollType: 'line-by-line',
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.5,
      textAlign: 'center',
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    speakerName: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    speakerUsername: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
  },
  transcriptions: {
    square: {},
    portrait: {
      fontSize: 72,
      lineHeight: 1.7,
      linesPerPage: 6,
    },
    landscape: {
      fontSize: 58,
      lineHeight: 1.69,
      linesPerPage: 6,
    },
  },
}
